import { Analytics } from "@mui/icons-material";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import PropertyService from "../../../api/PropertyService";
import BarChart from "../../../components/BarChart/BarChart";
import { CustomButton } from "../../../components/CustomButton/CustomButton";
import { Filters } from "../../../components/Filters/Filters";
import { LoadingSpinner } from "../../../components/LoadingSpinner/LoadingSpinner";
import { MonitorSummary } from "../../../components/MonitorSummary/MonitorSummary";
import { useAnalysis } from "../../../hooks/useAnalysis";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { DeficiencyIcon } from "../../../icons/DeficiencyIcon/DeficiencyIcon";
import { AppLayout } from "../../../layout/AppLayout/AppLayout";
import { RootState } from "../../../store";
import styles from "../../../styles/customer-analysis.module.css";

import { MonitorTypeItem } from "./Monitors";

export const CustomerAnalysis = () => {
  const [t] = useTranslation();
  const { isDesktop } = useScreenSize();

  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [monitorTypes, setMonitorTypes] = useState<MonitorTypeItem[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [filteredPropertyAreas, setFilteredPropertyAreas] = useState<string[]>(
    []
  );
  const [selectedAppointmentTypes, setSelectedAppointmentTypes] = useState<
    string[]
  >([]);
  const [selectedMonitorTypes, setSelectedMonitorTypes] = useState<string[]>(
    []
  );
  const [filtered, setFiltered] = useState(false);
  const [selectedView, setSelectedView] = useState<"summary" | "table">(
    "summary"
  );

  const selectedPropertyId = useSelector(
    (state: RootState) => state.user.selectedPropertyId
  );

  const appointmentTypes = [
    { id: 1, name: "Monitoring", value: "monitoring" },
    { id: 2, name: "Mangelauftrag", value: "mangelauftrag" },
    { id: 3, name: "Bekämpfung", value: "auftrag" },
  ];

  const deficiencyTypes = [
    { id: 1, name: "Baulich", value: "facility" },
    { id: 2, name: "Hygenisch", value: "hygienic" },
    { id: 3, name: "Organisatorische", value: "organizational" },
    { id: 4, name: "Sonstige", value: "other" },
  ];

  useEffect(() => {
    if (selectedPropertyId) {
      PropertyService.GetProperty(selectedPropertyId).then((property) => {
        const areas =
          property?.data?.propertyAreas
            ?.filter((area) => !area.isHidden)
            .map((area) => ({
              id: area.id,
              name: area.label,
              map: area.map,
              monitors: area.includes.monitors,
            })) ?? [];

        setPropertyAreas(areas);
      });

      PropertyService.GetPropertyMonitorTypes(selectedPropertyId).then(
        (response) => {
          if (response?.data) {
            setMonitorTypes(response.data);
          }
        }
      );
    }
  }, [selectedPropertyId]);

  const {
    appointmentAnalysisData,
    deficiencyAnalysisData,
    // infestationAnalysisData,
    pestCounts,
    monitorSummaryAnalysisData,
    monitorTableAnalysisItems,
    // countAppointment,
    loading,
  } = useAnalysis(
    selectedAppointmentTypes,
    fromDate,
    toDate,
    filteredPropertyAreas,
    selectedMonitorTypes
  );

  const handleOpenFilter = () => {
    setShowFilter(true);
  };

  const handleCloseFilter = () => {
    setShowFilter(false);
  };

  const handleApplyFilter = (
    fromDate: string,
    toDate: string,
    propertyAreas: string[],
    appointmentTypes: string[],
    selectedStationar: string[],
    monitorTypes: string[]
  ) => {
    setShowFilter(false);
    setFromDate(fromDate);
    setToDate(toDate);
    setFilteredPropertyAreas(propertyAreas);
    setSelectedAppointmentTypes(appointmentTypes);
    setSelectedMonitorTypes(monitorTypes);

    if (
      fromDate ||
      toDate ||
      propertyAreas.length ||
      appointmentTypes.length ||
      monitorTypes.length
    )
      setFiltered(true);
    else setFiltered(false);
  };

  const handleResetFilter = () => {
    setFromDate("");
    setToDate("");
    setFilteredPropertyAreas([]);
    setSelectedAppointmentTypes([]);
    setSelectedMonitorTypes([]);
    setFiltered(false);
  };

  return (
    <AppLayout>
      <div className={styles["customer-analysis"]}>
        <div className={styles["customer-analysis__heading"]}>
          <p className={styles["customer-analysis__title"]}>
            {isDesktop && <Analytics />} {t("Analysis")}
          </p>

          <div className={styles["filter-wrapper"]}>
            {showFilter ? (
              <Filters
                showAreaFilter
                showDateFilter
                showAppointmentTypesFilter
                showMonitorTypesFilter
                fromDate={fromDate ? new Date(fromDate) : null}
                toDate={toDate ? new Date(toDate) : null}
                areas={filteredPropertyAreas}
                appointmentTypesOptions={appointmentTypes}
                types={selectedAppointmentTypes}
                propertyAreaOptions={propertyAreas}
                monitorTypesOptions={monitorTypes}
                monitorTypes={selectedMonitorTypes}
                onClose={handleCloseFilter}
                onApplyFilter={handleApplyFilter}
                buttonWidth={60}
              />
            ) : (
              <div className={styles["reset"]}>
                {filtered && (
                  <p
                    className={styles["filters__reset-text"]}
                    onClick={handleResetFilter}
                  >
                    {t("ResetFilter")}{" "}
                  </p>
                )}
                <CustomButton
                  icon={filtered ? "filterLight" : "filterDark"}
                  color={filtered ? "green" : "lightGray"}
                  width={48}
                  onClick={handleOpenFilter}
                />
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <LoadingSpinner loading={loading} />
        ) : (
          <div className={styles["charts"]}>
            {pestCounts.length > 0 ? (
              <BarChart
                title={t("NumberOfPests")}
                data={pestCounts}
                noPadding
              />
            ) : (
              <div className={styles["chart-placeholder"]}>
                <p className={styles["no-results"]}>{t("NoResults")}</p>
              </div>
            )}

            <MonitorSummary
              data={monitorSummaryAnalysisData}
              tableData={monitorTableAnalysisItems}
              selectedView={selectedView}
              onViewChange={setSelectedView}
            />

            {selectedView === "summary" && (
              <>
                {!(
                  selectedMonitorTypes.length > 0 &&
                  !fromDate &&
                  !toDate &&
                  filteredPropertyAreas.length === 0 &&
                  selectedAppointmentTypes.length === 0
                ) && (
                  <div className={styles["appointments-and-deficiencies"]}>
                    {appointmentAnalysisData &&
                    appointmentAnalysisData.length > 0 ? (
                      <div className={styles["appointment-analysis-container"]}>
                        <span className={styles["appointment-analysis-title"]}>
                          {t("LastServices")}
                        </span>
                        <div className={styles["appointment-analysis"]}>
                          {appointmentTypes.map((type, index) => {
                            const item = appointmentAnalysisData.find(
                              (d) => d.type === type.value
                            );

                            return (
                              <Fragment key={type.value}>
                                <div
                                  className={
                                    styles["appointment-analysis-item"]
                                  }
                                >
                                  {item?.count ?? 0}{" "}
                                  <span
                                    className={
                                      styles["appointment-analysis-item--label"]
                                    }
                                  >
                                    {type.name}
                                  </span>
                                </div>

                                {index < appointmentTypes.length - 1 && (
                                  <div className={styles["separator"]} />
                                )}
                              </Fragment>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className={styles["chart-placeholder"]}>
                        <p className={styles["no-results"]}>{t("NoResults")}</p>
                      </div>
                    )}

                    {deficiencyAnalysisData &&
                    deficiencyAnalysisData.length > 0 ? (
                      <div className={styles["deficiency-analysis-container"]}>
                        <span className={styles["appointment-analysis-title"]}>
                          {t("Deficiency")}
                        </span>
                        <div className={styles["deficiency-analysis"]}>
                          {deficiencyTypes.map((type, index) => {
                            const item = deficiencyAnalysisData.find(
                              (d) => d.type === type.value
                            );

                            return (
                              <Fragment key={type.value}>
                                {isDesktop ? (
                                  <DeficiencyIcon
                                    type={type.value as any}
                                    count={item?.count ?? 0}
                                  />
                                ) : (
                                  <div
                                    className={
                                      styles["deficiency-analysis-item"]
                                    }
                                  >
                                    <span
                                      className={
                                        styles[
                                          "appointment-analysis-item--label"
                                        ]
                                      }
                                    >
                                      {type.name}
                                    </span>
                                    {item?.count ?? 0}
                                  </div>
                                )}
                              </Fragment>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className={styles["chart-placeholder"]}>
                        <p className={styles["no-results"]}>{t("NoResults")}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* {infestationAnalysisData.length > 0 && <PieChartComponent
              title={t("InfestationAnalysis")}
              subtitle={t("InfestationAnalysisSubtitle", { countAppointment })}
              data={infestationAnalysisData}
            />} */}
              </>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
};
